import { useQueryClient } from '@tanstack/react-query'
import { Button, Card, Flex, Tabs } from 'antd'
import { useMemo, useState } from 'react'
import { Outlet, useNavigate, useParams } from 'react-router-dom'

import {
  useCounterpartServiceGetInternalInstitutionEndpoint,
  useInvestigationsServiceGetTbCaseStatusEndpoint,
  useInvestigationsServiceTransmitTbCaseEndpoint,
  usePatientsServiceSignupPersonEndpoint,
  usePersonsServiceGetListPersonInstitutionsHistoryEndpoint,
  usePersonsServiceGetPersonByIdEndpointKey,
  useSimeTbServiceGetSimeTbPersonByIdnpEndpoint,
  useTreatmentServiceCheckPregnancyHistoryEndpoint
} from 'src/api/queries'
import {
  ApiError,
  Gender,
  MedicalAssistanceType,
  Permissions
} from 'src/api/requests'
import {
  LoadingScreen,
  notification,
  PageBreadcrumbs,
  SaveConfirm
} from 'src/components'
import { MAX_PREGNANCY_AGE, MIN_PREGNANCY_AGE } from 'src/features/persons'
import {
  ActiveSimeTbTreatmentCard,
  PersonSummaryCard,
  PersonSummaryDetailList,
  TransferModal
} from 'src/features/persons/components'
import { useUser } from 'src/hooks'
import { getUserAge } from 'src/libs'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'
import { formErrorHandler } from 'src/utils'

import { CD4Chart } from '../components/CD4Chart'
import { usePatientBreadcrumb, usePatientInformation } from '../hooks'

type DetailRoute = {
  key: string
  path: (id?: string) => string
  permission?: Permissions
  condition?: boolean
}

export const PatientDetailsPage = () => {
  const { t } = useLocales()
  const { id } = useParams()

  const { hasPermission } = useUser()

  const { breadcrumbItems, isLoadingPerson, person } = usePatientBreadcrumb(id)

  const {
    patientInformationInfo,
    patientDiagnosticInfo,
    patientTreatmentInfo
  } = usePatientInformation(id)
  const navigate = useNavigate()

  const queryClient = useQueryClient()
  const [isModalOpen, setIsModalOpen] = useState(false)

  const onToggle = () => setIsModalOpen((prev) => !prev)

  const { data: pregnancyHistory, isLoading: isPregnancyHistoryLoading } =
    useTreatmentServiceCheckPregnancyHistoryEndpoint(
      { id: String(id) },
      [String(id)],
      { enabled: !!id && person?.gender === Gender.FEMALE }
    )

  const { data: transferHistory, isLoading: isLoadingTransferHistory } =
    usePersonsServiceGetListPersonInstitutionsHistoryEndpoint(
      {
        personId: String(id)
      },
      [String(id)],
      { enabled: !!id }
    )

  const { data: institution } =
    useCounterpartServiceGetInternalInstitutionEndpoint(
      {
        id: String(person?.institution?.id)
      },
      [String(person?.institution?.id)],
      { enabled: !!person?.institution?.id }
    )

  const isPenitentiary =
    institution?.structure?.medicalAssistanceTypes?.includes(
      MedicalAssistanceType.PENITENTIARY
    )

  const { data: simeTBPerson } = useSimeTbServiceGetSimeTbPersonByIdnpEndpoint(
    {
      idnp: String(person?.idnp)
    },
    [String(person?.idnp)],
    { enabled: !!person }
  )

  const canViewSimeTb = hasPermission(
    Permissions.INTEGRATIONS_SIME_TB_PERSONS_VIEW
  )

  // TB confirmed (positive TB investigation, or a positive Xpert/LF-LAM in a
  // consultation TB screening) and not yet sent to SIME TB.
  const { data: tbCaseStatus, refetch: refetchTbCaseStatus } =
    useInvestigationsServiceGetTbCaseStatusEndpoint(
      { personId: String(id) },
      [String(id)],
      { enabled: !!id && canViewSimeTb }
    )

  const canTransmitTbCase =
    !!tbCaseStatus?.isTbPositive && !tbCaseStatus?.isTransmitted

  const personAge = getUserAge(String(person?.birthDate))

  const showPregnantBlock =
    person?.gender === Gender.FEMALE &&
    personAge >= MIN_PREGNANCY_AGE &&
    personAge <= MAX_PREGNANCY_AGE

  const canTransferPatient =
    hasPermission(Permissions.PERSONS_TRANSFER_REQUEST_CREATE_INSTITUTION) ||
    isPenitentiary

  const personDetailsMap = useMemo(() => {
    if (!person) return []

    const detailRoutes: DetailRoute[] = [
      {
        key: 'details',
        path: (id?: string) => paths.patients.details(id)
      },
      {
        key: 'risk-groups',
        path: () => paths.patients.riskGroups.root(),
        permission: Permissions.PERSONS_MENU_ACCESS_PATIENTS
      },
      {
        key: 'consultations',
        path: () => paths.patients.consultations.root(),
        permission: Permissions.TREATMENT_CONSULTATION_VIEW_INSTITUTION
      },
      {
        key: 'diagnoses',
        path: () => paths.patients.diagnoses.root(),
        permission: Permissions.TREATMENT_CONSULTATION_VIEW_INSTITUTION
      },
      {
        key: 'investigations',
        path: () => paths.patients.investigations.root()
      },
      {
        key: 'epidemiology-cases',
        path: () => paths.patients.epidemiologyCases.root()
      },
      {
        key: 'treatments',
        path: () => paths.patients.treatments.root(),
        permission: Permissions.TREATMENT_TREATMENT_PLAN_VIEW_INSTITUTION
      },
      {
        key: 'recipes',
        path: () => paths.patients.recipes.root(),
        permission:
          Permissions.TREATMENT_MENU_ACCESS_TAB_TREATMENT_PLAN_RECIPE_PATIENT
      },
      {
        key: 'pregnancy',
        path: () => paths.patients.pregnancy.root(),
        permission: Permissions.TREATMENT_MENU_ACCESS_PREGNANCY_MONITORING,
        condition:
          !!pregnancyHistory?.hasPregnancyMonitoring && showPregnantBlock
      },
      {
        key: 'quiz',
        path: () => paths.patients.quiz.root(),
        permission:
          Permissions.QUESTIONNAIRES_MENU_ACCESS_PATIENT_QUESTIONNAIRES
      },
      {
        key: 'transfer-history',
        path: () => paths.patients.transfers.root(),
        condition: (transferHistory?.totalCount ?? 0) > 0
      }
    ]

    return detailRoutes.filter(({ permission, condition }) => {
      const hasPermissionAccess = permission ? hasPermission(permission) : true
      const meetsCondition = condition !== false

      return hasPermissionAccess && meetsCondition
    })
  }, [person, showPregnantBlock, transferHistory, pregnancyHistory])

  const segmentedOptions = useMemo(
    () =>
      personDetailsMap.map(({ key }) => ({
        key,
        label: t(`persons.person_${key}`)
      })),
    [t, personDetailsMap]
  )

  const activeKey = useMemo(() => {
    const lastSegment = location.pathname.split('/').filter(Boolean).pop()

    // The investigation-results page is a drill-down from the investigations
    // table, not its own tab — keep the investigations tab active.
    if (lastSegment === 'investigation-results') {
      return 'investigations'
    }

    return (
      personDetailsMap.find(({ key }) => key === lastSegment)?.key ?? 'details'
    )
  }, [location.pathname])

  const onChange = (val: string | number) => {
    const selectedTab = personDetailsMap.find(({ key }) => key === val)
    if (selectedTab) {
      navigate(selectedTab.path(String(id)))
    }
  }

  const { mutate: createPersonAccount } =
    usePatientsServiceSignupPersonEndpoint({
      onError: (e: ApiError) => formErrorHandler(e.body.errors),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [usePersonsServiceGetPersonByIdEndpointKey, String(id)]
        })
        notification.success({
          message: t('persons.create_patient_account_success')
        })
      }
    })

  const onCreateAccount = () => {
    createPersonAccount({
      id: String(id)
    })
  }

  const { mutate: transmitTbCase, isPending: isNotifyingTb } =
    useInvestigationsServiceTransmitTbCaseEndpoint({
      onError: (e: ApiError) => formErrorHandler(e.body.errors),
      onSuccess: () => {
        notification.success({
          message: t('persons.notify_tb_new_case_success')
        })
        void refetchTbCaseStatus()
      }
    })

  const summaryCards = [
    {
      title: t('persons.personal_details_card'),
      variant: 'purple' as const,
      items: patientInformationInfo
    },
    {
      title: t('persons.diagnostic_card'),
      variant: 'green' as const,
      items: patientDiagnosticInfo
    },
    {
      title: t('persons.treatment_card'),
      variant: 'yellow' as const,
      items: patientTreatmentInfo
    }
  ]

  if (
    isLoadingPerson ||
    isLoadingTransferHistory ||
    isPregnancyHistoryLoading
  ) {
    return <LoadingScreen />
  }

  return (
    <PageBreadcrumbs
      items={breadcrumbItems}
      patientInformation={
        <Flex vertical gap={16} style={{ width: '100%', minWidth: 0 }}>
          <Flex gap={8} wrap="wrap">
            {summaryCards.map(
              ({ title, variant, items }) =>
                !!items.length && (
                  <PersonSummaryCard
                    key={title}
                    title={title}
                    variant={variant}
                    statusText={
                      <PersonSummaryDetailList
                        variant={variant}
                        items={items.map((item) => ({
                          label: String(item.label),
                          value: item.children
                        }))}
                      />
                    }
                  />
                )
            )}
          </Flex>
          <ActiveSimeTbTreatmentCard personId={id} idnp={person?.idnp} />
          <CD4Chart />
        </Flex>
      }
      extra={
        <Flex gap={16}>
          <Flex gap={16} justify="end">
            {!person?.userProfileId && !!patientDiagnosticInfo?.length && (
              <SaveConfirm
                title={`${t('persons.create_patient_account')}?`}
                placement="leftBottom"
                onConfirm={onCreateAccount}
              >
                <Button>{t('persons.create_patient_account')}</Button>
              </SaveConfirm>
            )}

            {canTransferPatient && (
              <Button type="primary" onClick={onToggle}>
                {t('persons.transfer')}
              </Button>
            )}

            {canViewSimeTb && simeTBPerson && (
              <Button
                type="primary"
                onClick={() =>
                  navigate(
                    paths.patients.simeTB.root(person?.id, simeTBPerson?.id)
                  )
                }
              >
                {t('persons.sime_tb_access')}
              </Button>
            )}

            {canViewSimeTb && canTransmitTbCase && (
              <SaveConfirm
                title={`${t('persons.notify_tb_new_case')}?`}
                onConfirm={() => transmitTbCase({ personId: String(id) })}
              >
                <Button type="primary" danger loading={isNotifyingTb}>
                  {t('persons.notify_tb_new_case')}
                </Button>
              </SaveConfirm>
            )}
          </Flex>
        </Flex>
      }
    >
      <Card>
        <Flex vertical gap={32}>
          <Tabs
            type="card"
            items={segmentedOptions}
            activeKey={activeKey}
            onChange={onChange}
          />
          <Outlet />
        </Flex>
      </Card>

      {isModalOpen && <TransferModal data={person} onCancel={onToggle} />}
    </PageBreadcrumbs>
  )
}
